import { AnyToken } from 'src/common/types/models/Tokens'
import { BigSource } from 'big.js'
import { useCallback } from 'react'
import { PreparedTransaction } from 'thirdweb'
import { safeTransferFrom as transferERC1155 } from 'thirdweb/extensions/erc1155'
import { transferFrom as transferERC721 } from 'thirdweb/extensions/erc721'
import { transfer as transferERC20 } from 'thirdweb/extensions/erc20'
import useGetContract from '../common/useGetContract'
import useConfig from 'src/config/hooks/useConfig'
import useTransferCarbonNfts from '../carbom-nft/useTransferCarbonNfts'

type TransferParams = {
  from?: string // Required for ERC721 and ERC1155
  to: string
  amount?: BigSource // Required for ERC20 and ERC1155
  tokenId?: string | number // Required for ERC721 and ERC1155
  tokenIds?: (number | string)[]
  token: AnyToken
}

const useTransferToken = () => {
  const config = useConfig()
  const { transferNfts: transferCarbonNfts } = useTransferCarbonNfts()
  const { getContract } = useGetContract()

  const transferToken = useCallback(
    ({ token, from, to, amount, tokenId, tokenIds }: TransferParams) => {
      const contract = getContract(token.contractAddress)
      if (!contract) return

      const tokenType = token.type
      let tx: PreparedTransaction | undefined
      if (token.contractAddress === config.tokensAddresses.vicc) {
        if (!amount) throw new Error('Amount is required for Carbon transfer')
        tx = transferCarbonNfts({
          to,
          amount: +amount.toString(),
          tokenIds
        })
      } else if (tokenType === 'ERC20') {
        if (!amount) throw new Error('Amount is required for ERC20 transfer')
        tx = transferERC20({
          contract,
          to,
          amount: amount.toString(),
        })
      } else if (tokenType === 'ERC721') {
        if (!from)
          throw new Error('From address is required for ERC721 transfer')
        if (!tokenId)
          throw new Error('Token ID is required for ERC721 transfer')
        tx = transferERC721({
          contract,
          to,
          from,
          tokenId: BigInt(tokenId),
        })
      } else if (tokenType === 'ERC1155') {
        if (!from)
          throw new Error('From address is required for ERC1155 transfer')
        if (!tokenId || !amount) {
          throw new Error(
            'Token ID and amount are required for ERC1155 transfer'
          )
        }
        tx = transferERC1155({
          contract,
          to,
          from,
          tokenId: BigInt(tokenId),
          value: BigInt(amount.toString()),
          data: '0x',
        })
      }

      return tx
    },
    [config.tokensAddresses.vicc, getContract, transferCarbonNfts]
  )

  return transferToken
}

export default useTransferToken
